import { useSyncExternalStore } from 'react';
import type { AggregateRow, Tournament } from '@airmail/engine';
import { clubKey, clubStandings, pairingKey, phaseStandings, playerStandings, teamAggregates, teamKey, teamNumber, rankAggregates } from '@airmail/engine';
import { db, newId } from './db';
import type { EntityTable } from 'dexie';
import { currentUser } from './auth';
import { DEFAULT_API, apiCall } from './sync';

/**
 * Saison (docs/13 §4): mehrere Spieltage, deren Wertungen aufsummiert werden.
 * Ein Spieltag wird als **Kopie** seiner Tabellen übernommen — die Saison
 * bleibt vollständig, auch wenn das Turnier später gelöscht wird.
 */
export interface SeasonDay {
  tournamentId: string;
  name: string;
  date?: string;
  importedAt: number;
  clubs: AggregateRow[];
  teams: AggregateRow[];
  players: AggregateRow[];
  /** Kader des Spieltags (Mannschaft → Spieler in Paar-Reihenfolge), damit der nächste Spieltag damit starten kann */
  roster?: RosterEntry[];
  /**
   * Aufstellung des Spieltags als Teams (docs/13 §4, seit 09/2026): Name,
   * Mannschaft, Spieler. Anders als `roster` behält sie die Teamnamen — die
   * Saison erkennt „Ceva 1" am nächsten Spieltag daran wieder.
   */
  lineup?: LineupEntry[];
  /**
   * Gewertete Spiele des Spieltags zwischen Teams (Schlüssel = teamKey). Aus
   * ihnen weiß die Saison, welche Begegnungen noch ausstehen — Harald: „In
   * einer Saison werden alle Begegnungen ausgetragen … es wäre toll zu
   * erkennen, welche noch ausstehen." Spieltage aus der Zeit davor haben das
   * nicht; erneutes „In Saison übernehmen" holt es nach.
   */
  games?: SeasonGame[];
  /** Der Spieltag lief in Leistungsgruppen (docs/13 §7) — setzt beim Übernehmen die Saison darauf */
  leistungsgruppen?: boolean;
}

export interface RosterEntry {
  club: string;
  players: { firstName: string; lastName: string; substitute?: boolean }[];
}

export interface LineupEntry {
  key: string;
  name: string;
  club?: string;
  players: { firstName: string; lastName: string; substitute?: boolean }[];
}

export interface SeasonGame {
  a: string;
  b: string;
  /** Sieger aus Sicht von a/b, `draw` bei Unentschieden */
  result: 'a' | 'b' | 'draw';
  sa: number;
  sb: number;
  walkover?: boolean;
}

export interface Season {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  days: SeasonDay[];
  /**
   * Mannschaften außer Wertung (clubKey): z. B. eine, die erst spät eingestiegen
   * ist und nicht mehr gegen alle spielen kann (Harald, 09/2026). Sie bleibt
   * sichtbar, aber ohne Rang, und ihre offenen Begegnungen zählen nicht.
   */
  excluded?: string[];
  /**
   * Leistungsgruppen (Harald, 09/2026, docs/13 §7): Über die Saison spielt
   * jedes Paar nur gegen die Paare **gleicher Nummer** der anderen Mannschaften
   * („Ceva 1“ gegen „Nucetto 1“, nie gegen „Nucetto 2“). Wird vom ersten
   * Spieltag mit Leistungsgruppen gesetzt und lässt sich auf der Saison-Seite
   * umschalten. Ohne den Schalter: jedes Paar gegen alle Paare der anderen
   * Mannschaften.
   */
  leistungsgruppen?: boolean;
  /**
   * Kader der Saison (Daniel, 09/2026): die festen Plätze je Mannschaft („Ceva 1")
   * mit den Personen, die gerade darin spielen. Füllt sich aus jedem
   * übernommenen Spieltag, lässt sich auf der Saison-Seite bearbeiten, und
   * der Assistent lädt ihn für den nächsten Spieltag — kein Import, keine
   * Tippfehler, gleiche Namen. Harald: „Die Personen, die eine Mannschaft
   * bilden, können von Spieltag zu Spieltag wechseln" — der Platz bleibt.
   */
  kader?: LineupEntry[];
  /** Stand (updatedAt), der zuletzt mit dem Konto abgeglichen wurde — fehlt bei rein lokalen Saisons */
  cloudAt?: number;
  /**
   * Konto (Cognito-`sub`), dem die Saison gehört. Saisons lagen vorher je
   * Browser, nicht je Konto: Meldete sich B nach A an, wurden A's Saisons hier
   * gelöscht (in B's Konto fehlten sie ja) oder in B's Konto hochgeladen.
   * Fehlt das Feld, ist die Saison ohne Anmeldung entstanden (oder älter).
   */
  owner?: string;
  /**
   * Grabstein: lokal gelöscht, auf dem Server noch nicht. Ohne ihn kam eine
   * offline gelöschte Saison beim nächsten Abgleich aus der Cloud zurück.
   */
  deleted?: true;
}

const seasons = () => db.table('seasons') as EntityTable<Season, 'id'>;

/**
 * Saisons hängen am Organisatoren-Konto (docs/16): Ist jemand angemeldet, wird
 * vor dem Listen mit dem Server abgeglichen — der jüngere Stand gewinnt, auf
 * einem anderen Gerät gelöschte Saisons verschwinden auch hier. Ohne Login oder
 * ohne Netz bleibt alles lokal und wird beim nächsten Mal nachgezogen.
 */
const cloudOn = () => !!currentUser() && !!DEFAULT_API;

/** Gehört die Saison zum angemeldeten Konto (oder zu keinem)? Ohne Anmeldung zählt alles */
function sichtbar(s: Season, sub: string | undefined): boolean {
  return !s.deleted && (!sub || !s.owner || s.owner === sub);
}

// --- Cloud-Fehler sichtbar machen ---------------------------------------------------

/**
 * Letzter Fehler beim Abgleich mit dem Konto. Speichern, Anlegen und Löschen
 * schluckten ihn vorher — die Saison stand lokal, fehlte aber in der Cloud,
 * und niemand erfuhr es. Lokal gespeichert wird trotzdem immer; der nächste
 * Abgleich holt nach.
 */
let cloudFehler: string | null = null;
const fehlerHoerer = new Set<() => void>();
function fehlerSetzen(e: string | null): void {
  if (cloudFehler === e) return;
  cloudFehler = e;
  for (const fn of fehlerHoerer) fn();
}
export function saisonCloudFehler(): string | null {
  return cloudFehler;
}
export function useSaisonCloudFehler(): string | null {
  return useSyncExternalStore(
    (fn) => {
      fehlerHoerer.add(fn);
      return () => fehlerHoerer.delete(fn);
    },
    () => cloudFehler,
  );
}
export function saisonCloudFehlerQuittieren(): void {
  fehlerSetzen(null);
}

/** Ergebnis von Speichern/Löschen: lokal ist es immer geschehen, `cloud` sagt, ob auch im Konto */
export interface SaisonCloud {
  cloud: 'ok' | 'aus' | 'fehler';
  error?: string;
}
function fehlerAus(e: unknown): SaisonCloud {
  const error = (e as Error)?.message ?? String(e);
  fehlerSetzen(error);
  return { cloud: 'fehler', error };
}

export async function syncSeasons(): Promise<void> {
  const sub = currentUser()?.sub;
  if (!cloudOn() || !sub) return;
  const remote = await apiCall<{ seasons: { id: string; updatedAt: number; data: Season }[] }>(DEFAULT_API, undefined, 'GET', '/me/seasons');
  // Nur Saisons dieses Kontos (und herrenlose) — die anderer Konten am selben Gerät bleiben unberührt
  const local = (await seasons().toArray()).filter((s) => !s.owner || s.owner === sub);
  const byId = new Map(local.map((s) => [s.id, s]));
  for (const r of remote.seasons) {
    const l = byId.get(r.id);
    byId.delete(r.id);
    if (l?.deleted) {
      // offline gelöscht: der Grabstein gewinnt
      await apiCall(DEFAULT_API, undefined, 'DELETE', `/me/seasons/${encodeURIComponent(r.id)}`);
      await seasons().delete(r.id);
    } else if (!l || r.updatedAt > l.updatedAt) {
      const { cloudAt: _c, owner: _o, deleted: _d, ...data } = r.data;
      await seasons().put({ ...data, id: r.id, updatedAt: r.updatedAt, cloudAt: r.updatedAt, owner: sub });
    } else if (l.updatedAt > r.updatedAt) await pushSeason({ ...l, owner: sub });
    else if (l.cloudAt !== r.updatedAt || l.owner !== sub) await seasons().put({ ...l, cloudAt: r.updatedAt, owner: sub });
  }
  for (const l of byId.values()) {
    if (l.deleted) {
      // Auf dem Server gibt es sie nicht (mehr) — Grabstein abräumen
      await seasons().delete(l.id);
    } else if (l.cloudAt === undefined) {
      // nie in der Cloud gewesen → hochladen (eine herrenlose Saison kommt damit in dieses Konto)
      await pushSeason({ ...l, owner: sub });
    } else if (l.owner === sub) {
      // war in der Cloud dieses Kontos und ist dort weg → auf anderem Gerät gelöscht
      await seasons().delete(l.id);
    }
    // war in einer Cloud, aber ohne Eigentümer (vor 09/2026): nicht zu entscheiden, wessen — liegen lassen
  }
}

async function pushSeason(s: Season): Promise<void> {
  const { cloudAt: _c, owner: _o, deleted: _d, ...data } = s;
  await apiCall(DEFAULT_API, undefined, 'PUT', `/me/seasons/${encodeURIComponent(s.id)}`, { updatedAt: s.updatedAt, data });
  await seasons().put({ ...s, cloudAt: s.updatedAt });
}

export async function listSeasons(): Promise<Season[]> {
  await syncSeasons().then(
    () => fehlerSetzen(null),
    (e: unknown) => {
      // offline: kein Fehler, nur lokaler Stand; sonst sagen, dass die Cloud hängt
      if (typeof navigator === 'undefined' || navigator.onLine !== false) fehlerAus(e);
    },
  );
  const sub = currentUser()?.sub;
  return (await seasons().orderBy('updatedAt').reverse().toArray()).filter((s) => sichtbar(s, sub));
}
export async function loadSeason(id: string): Promise<Season | undefined> {
  const s = await seasons().get(id);
  return s && sichtbar(s, currentUser()?.sub) ? s : undefined;
}
/** Speichert immer lokal; das Ergebnis sagt, ob es auch im Konto angekommen ist (Fehler zusätzlich über `useSaisonCloudFehler`) */
export async function saveSeason(s: Season): Promise<SaisonCloud> {
  const sub = currentUser()?.sub;
  const next: Season = { ...s, updatedAt: Date.now() };
  if (!next.owner && sub) next.owner = sub;
  await seasons().put(next);
  if (!cloudOn() || (next.owner && next.owner !== sub)) return { cloud: 'aus' };
  try {
    await pushSeason(next);
    fehlerSetzen(null);
    return { cloud: 'ok' };
  } catch (e) {
    return fehlerAus(e);
  }
}
/**
 * Löschen: lokal sofort. War die Saison schon in der Cloud und klappt das
 * Löschen dort gerade nicht (offline, abgemeldet), bleibt ein Grabstein, den
 * der nächste Abgleich einlöst.
 */
export async function deleteSeason(id: string): Promise<SaisonCloud> {
  const s = await seasons().get(id);
  if (!s) return { cloud: 'aus' };
  const grabstein = async () =>
    seasons().put({
      id: s.id,
      name: s.name,
      createdAt: s.createdAt,
      updatedAt: Date.now(),
      days: [],
      deleted: true,
      ...(s.owner ? { owner: s.owner } : {}),
      ...(s.cloudAt !== undefined ? { cloudAt: s.cloudAt } : {}),
    });
  if (s.cloudAt === undefined) {
    await seasons().delete(id);
    return { cloud: 'aus' };
  }
  if (!cloudOn() || (s.owner && s.owner !== currentUser()?.sub)) {
    await grabstein();
    return { cloud: 'aus' };
  }
  try {
    await apiCall(DEFAULT_API, undefined, 'DELETE', `/me/seasons/${encodeURIComponent(id)}`);
    await seasons().delete(id);
    fehlerSetzen(null);
    return { cloud: 'ok' };
  } catch (e) {
    await grabstein();
    return fehlerAus(e);
  }
}
/** Legt lokal an und versucht, sie ins Konto zu laden; ein Cloud-Fehler steht danach in `useSaisonCloudFehler` */
export async function createSeason(name: string): Promise<Season> {
  const sub = currentUser()?.sub;
  const s: Season = { id: newId(), name, createdAt: Date.now(), updatedAt: Date.now(), days: [], ...(sub ? { owner: sub } : {}) };
  await seasons().put(s);
  if (cloudOn()) {
    try {
      await pushSeason(s);
      fehlerSetzen(null);
      return { ...s, cloudAt: s.updatedAt };
    } catch (e) {
      fehlerAus(e);
    }
  }
  return s;
}

/** Tabellen eines Spieltags (erste Gruppenphase des ersten Bewerbs) als Saison-Eintrag */
export function dayFromTournament(t: Tournament): SeasonDay | null {
  const comp = t.competitions[0];
  const phase = comp?.phases.find((p) => p.type === 'groups' && p.status !== 'planned');
  if (!comp || !phase) return null;
  const rows = phaseStandings(t, phase.id).flatMap((g) => g.rows);
  const day: SeasonDay = {
    tournamentId: t.id,
    name: t.name,
    importedAt: Date.now(),
    clubs: clubStandings(comp, rows),
    teams: teamAggregates(comp, rows),
    players: playerStandings(comp, rows),
    roster: rosterOf(t),
    lineup: lineupOf(t),
    games: gamesOf(t),
  };
  if (t.date) day.date = t.date;
  if (phase.config.type === 'groups' && phase.config.groupByTeamNumber) day.leistungsgruppen = true;
  return day;
}

/** Teams des Spieltags mit Schlüssel — zurückgezogene bleiben drin, sie haben ja gespielt */
export function lineupOf(t: Tournament): LineupEntry[] {
  const comp = t.competitions[0];
  return (comp?.teams ?? []).map((team) => {
    const e: LineupEntry = {
      key: teamKey(team),
      name: team.name,
      players: team.players.map((p) => ({ firstName: p.firstName, lastName: p.lastName, ...(p.substitute ? { substitute: true } : {}) })),
    };
    if (team.club) e.club = team.club;
    return e;
  });
}

/** Gewertete Spiele der Gruppenphase(n) als Begegnungen zwischen Team-Schlüsseln */
export function gamesOf(t: Tournament): SeasonGame[] {
  const comp = t.competitions[0];
  if (!comp) return [];
  const keyOf = new Map(comp.teams.map((team) => [team.id, teamKey(team)]));
  const out: SeasonGame[] = [];
  for (const phase of comp.phases) {
    if (phase.type !== 'groups') continue;
    for (const m of phase.matches) {
      if ((m.status !== 'finished' && m.status !== 'walkover') || m.annulled || !m.result || m.a.kind !== 'team' || m.b.kind !== 'team') continue;
      const a = keyOf.get(m.a.teamId);
      const b = keyOf.get(m.b.teamId);
      if (!a || !b) continue;
      const sa = m.result.sets.reduce((s, x) => s + x.a, 0);
      const sb = m.result.sets.reduce((s, x) => s + x.b, 0);
      const g: SeasonGame = { a, b, result: m.result.winner === 'A' ? 'a' : m.result.winner === 'B' ? 'b' : 'draw', sa, sb };
      if (m.status === 'walkover') g.walkover = true;
      out.push(g);
    }
  }
  return out;
}

/** Aufstellung als Text für den Assistenten: „Team<Tab>Spieler 1<Tab>Spieler 2<Tab>Mannschaft" — die Teamnamen bleiben so erhalten */
export function lineupText(lineup: LineupEntry[]): string {
  return lineup.map((e) => [e.name, ...e.players.map((p) => `${p.substitute ? '*' : ''}${p.firstName} ${p.lastName}`.trim()), e.club ?? ''].join('\t').replace(/\t$/, '')).join('\n');
}

/** Alle Teams, die in der Saison je aufgetaucht sind — jüngster Spieltag bestimmt Schreibweise */
export function seasonTeams(season: Season): Map<string, { key: string; name: string; club?: string; clubKey?: string }> {
  const map = new Map<string, { key: string; name: string; club?: string; clubKey?: string }>();
  for (const day of season.days) {
    if (day.lineup) {
      for (const e of day.lineup) {
        const entry: { key: string; name: string; club?: string; clubKey?: string } = { key: e.key, name: e.name };
        if (e.club) {
          entry.club = e.club;
          const ck = clubKey(e.club);
          if (ck) entry.clubKey = ck;
        }
        map.set(e.key, entry);
      }
    } else {
      // Alte Spieltage ohne Aufstellung: Name und Mannschaft stecken im Schlüssel „name (gemeinde)"
      for (const r of day.teams) {
        const m = /^(.*) \(([^()]*)\)$/.exec(r.key);
        const entry: { key: string; name: string; club?: string; clubKey?: string } = { key: r.key, name: r.name };
        if (m?.[2]) {
          entry.club = m[2];
          entry.clubKey = m[2];
        }
        map.set(r.key, entry);
      }
    }
  }
  return map;
}

/** Begegnungen, die an irgendeinem Spieltag schon gewertet wurden — als Schlüssel für `GroupsConfig.skipPairings` */
export function playedPairings(season: Season): Set<string> {
  const out = new Set<string>();
  for (const day of season.days) for (const g of day.games ?? []) out.add(pairingKey(g.a, g.b));
  return out;
}

export interface ClubPairing {
  a: { key: string; name: string };
  b: { key: string; name: string };
  total: number;
  played: number;
  open: { a: string; b: string }[];
}

/**
 * Stand der Saison als Spielplan: Welche Mannschaft hat gegen welche wie viele
 * der nötigen Begegnungen (jedes Paar gegen jedes fremde Paar) schon
 * ausgetragen, und welche Paarungen fehlen noch. Mannschaften außer Wertung
 * und Teams ohne Mannschaft bleiben außen vor.
 */
export function seasonPairings(season: Season): { clubs: ClubPairing[]; required: number; played: number; open: number; daysWithoutGames: SeasonDay[] } {
  const teams = [...seasonTeams(season).values()].filter((t) => t.clubKey && !(season.excluded ?? []).includes(t.clubKey));
  const done = playedPairings(season);
  const byPair = new Map<string, ClubPairing>();
  const clubName = new Map<string, string>();
  for (const t of teams) if (t.clubKey && t.club && !clubName.has(t.clubKey)) clubName.set(t.clubKey, t.club);
  for (let i = 0; i < teams.length; i++) {
    for (let j = i + 1; j < teams.length; j++) {
      const x = teams[i] as (typeof teams)[number];
      const y = teams[j] as (typeof teams)[number];
      if (x.clubKey === y.clubKey) continue;
      // Leistungsgruppen: nur gleiche Nummern begegnen sich über die Saison
      if (season.leistungsgruppen && teamNumber(x.name) !== teamNumber(y.name)) continue;
      const sorted = [x.clubKey as string, y.clubKey as string].sort();
      const ca = sorted[0] as string;
      const cb = sorted[1] as string;
      const pk = `${ca}|${cb}`;
      const cp: ClubPairing = byPair.get(pk) ?? { a: { key: ca, name: clubName.get(ca) ?? ca }, b: { key: cb, name: clubName.get(cb) ?? cb }, total: 0, played: 0, open: [] };
      cp.total++;
      if (done.has(pairingKey(x.key, y.key))) cp.played++;
      else cp.open.push(x.clubKey === ca ? { a: x.name, b: y.name } : { a: y.name, b: x.name });
      byPair.set(pk, cp);
    }
  }
  const clubs = [...byPair.values()].sort((p, q) => p.a.name.localeCompare(q.a.name, 'de') || p.b.name.localeCompare(q.b.name, 'de'));
  const required = clubs.reduce((s, c) => s + c.total, 0);
  const played = clubs.reduce((s, c) => s + c.played, 0);
  return { clubs, required, played, open: required - played, daysWithoutGames: season.days.filter((d) => !d.games) };
}

/** Spieler je Mannschaft in Paar-Reihenfolge (Ceva 1, Ceva 2 …), Ersatzspieler markiert */
export function rosterOf(t: Tournament): RosterEntry[] {
  const comp = t.competitions[0];
  const map = new Map<string, RosterEntry>();
  for (const team of comp?.teams ?? []) {
    const club = team.club ?? team.name;
    const entry = map.get(club) ?? { club, players: [] };
    for (const p of team.players) {
      const player: RosterEntry['players'][number] = { firstName: p.firstName, lastName: p.lastName };
      if (p.substitute) player.substitute = true;
      entry.players.push(player);
    }
    map.set(club, entry);
  }
  return [...map.values()];
}

/** Kader als Text für den Assistenten: eine Zeile je Spieler „Mannschaft<Tab>Name“, Ersatz mit Stern */
export function rosterText(roster: RosterEntry[]): string {
  return roster.flatMap((r) => r.players.map((p) => `${r.club}\t${p.substitute ? '*' : ''}${p.firstName} ${p.lastName}`.trim())).join('\n');
}

/** Jüngster Spieltag einer Saison, der einen Kader hat */
export function latestRoster(season: Season): { day: SeasonDay; roster: RosterEntry[] } | null {
  for (let i = season.days.length - 1; i >= 0; i--) {
    const day = season.days[i];
    if (day?.roster && day.roster.length > 0) return { day, roster: day.roster };
  }
  return null;
}

export function spieltagFileName(t: Tournament): string {
  return `airmail-spieltag-${(t.date ?? '').slice(0, 10) || 'ohne-datum'}-${t.name.replace(/[^\wäöüÄÖÜß-]+/g, '_')}.json`;
}

/** Spieltag-Datei lesen (docs/17) — Format wie von der Tabellen-Seite exportiert */
export function parseSpieltagFile(text: string): { ok: true; day: SeasonDay } | { ok: false; error: string } {
  try {
    const raw = JSON.parse(text) as { kind?: string; day?: Partial<SeasonDay> };
    const d = raw.day;
    if (raw.kind !== 'airmail-spieltag' || !d || typeof d.tournamentId !== 'string' || typeof d.name !== 'string' || !Array.isArray(d.clubs) || !Array.isArray(d.teams) || !Array.isArray(d.players)) {
      return { ok: false, error: 'Keine Airmail-Spieltag-Datei (Tabelle → „Spieltag als Datei“).' };
    }
    return { ok: true, day: { ...(d as SeasonDay), importedAt: Date.now() } };
  } catch {
    return { ok: false, error: 'Datei ist kein gültiges JSON.' };
  }
}

export function upsertDay(season: Season, day: SeasonDay): Season {
  const days = season.days.filter((d) => d.tournamentId !== day.tournamentId);
  days.push(day);
  days.sort((a, b) => (a.date ?? '').localeCompare(b.date ?? '') || a.importedAt - b.importedAt);
  // Der erste Spieltag mit Leistungsgruppen stellt die Saison darauf um; eine ausdrückliche Wahl auf der Saison-Seite bleibt
  const leistungsgruppen = season.leistungsgruppen ?? day.leistungsgruppen;
  return { ...season, days, kader: kaderMit(seasonKader(season), day.lineup ?? []), ...(leistungsgruppen !== undefined ? { leistungsgruppen } : {}) };
}

/** Kader um die Aufstellung eines Spieltags ergänzen: bekannte Plätze bekommen die aktuellen Personen, neue kommen dazu */
function kaderMit(kader: LineupEntry[], lineup: LineupEntry[]): LineupEntry[] {
  const out = kader.map((e) => ({ ...e, players: e.players.map((p) => ({ ...p })) }));
  for (const e of lineup) {
    const i = out.findIndex((x) => x.key === e.key);
    const kopie: LineupEntry = { key: e.key, name: e.name, players: e.players.map((p) => ({ ...p })) };
    if (e.club) kopie.club = e.club;
    if (i >= 0) out[i] = kopie;
    else out.push(kopie);
  }
  return out;
}

/** Kader der Saison — notfalls aus dem jüngsten Spieltag (Aufstellung oder alter Spielerliste) abgeleitet */
export function seasonKader(season: Season): LineupEntry[] {
  if (season.kader) return season.kader;
  for (let i = season.days.length - 1; i >= 0; i--) {
    const day = season.days[i];
    if (day?.lineup?.length) return day.lineup;
    if (day?.roster?.length) return kaderAusRoster(day.roster);
  }
  return [];
}

/** Alte Spielerliste (Mannschaft → Spieler in Paar-Reihenfolge) in Plätze „Mannschaft 1", „Mannschaft 2" … */
function kaderAusRoster(roster: RosterEntry[], teamSize = 2): LineupEntry[] {
  const out: LineupEntry[] = [];
  for (const r of roster) {
    const paare = Math.ceil(r.players.length / teamSize);
    for (let i = 0; i < paare; i++) {
      const name = paare > 1 ? `${r.club} ${i + 1}` : r.club;
      out.push({ key: teamKey({ name, club: r.club }), name, club: r.club, players: r.players.slice(i * teamSize, (i + 1) * teamSize).map((p) => ({ ...p })) });
    }
  }
  return out;
}

/** Eintrag mit passendem Schlüssel — für den Kader-Editor */
export function kaderEintrag(name: string, club: string, players: LineupEntry['players']): LineupEntry {
  const e: LineupEntry = { key: teamKey({ name: name.trim(), ...(club.trim() ? { club: club.trim() } : {}) }), name: name.trim(), players };
  if (club.trim()) e.club = club.trim();
  return e;
}

export interface SeasonRow extends AggregateRow {
  /** Punkte je Spieltag (Index wie season.days), undefined = nicht dabei */
  perDay: (number | undefined)[];
  daysPlayed: number;
  /** Mannschaft außer Wertung: steht unten, ohne Rang */
  excluded?: boolean;
}

/** Gehört ein Wertungsschlüssel zu einer Mannschaft außer Wertung? Mannschaft: gleich; Paar/Spieler: „… (gemeinde)" am Ende */
function ausserWertung(season: Season, kind: 'clubs' | 'teams' | 'players', key: string): boolean {
  const ex = season.excluded ?? [];
  if (ex.length === 0) return false;
  if (kind === 'clubs') return ex.includes(key);
  return ex.some((c) => key.endsWith(` (${c})`));
}

/** Summen über alle Spieltage je Schlüssel */
export function seasonTable(season: Season, kind: 'clubs' | 'teams' | 'players'): SeasonRow[] {
  const map = new Map<string, SeasonRow>();
  season.days.forEach((day, di) => {
    for (const r of day[kind]) {
      const cur = map.get(r.key) ?? {
        ...r,
        teamIds: [],
        rank: 0,
        played: 0,
        wins: 0,
        draws: 0,
        losses: 0,
        points: 0,
        scoreFor: 0,
        scoreAgainst: 0,
        scoreDiff: 0,
        pointsPerGame: 0,
        perDay: Array.from({ length: season.days.length }, () => undefined),
        daysPlayed: 0,
      };
      cur.name = r.name;
      cur.played += r.played;
      cur.wins += r.wins;
      cur.draws += r.draws;
      cur.losses += r.losses;
      cur.points += r.points;
      cur.scoreFor += r.scoreFor;
      cur.scoreAgainst += r.scoreAgainst;
      cur.scoreDiff = cur.scoreFor - cur.scoreAgainst;
      cur.pointsPerGame = cur.played > 0 ? cur.points / cur.played : 0;
      cur.perDay[di] = r.points;
      cur.daysPlayed++;
      map.set(r.key, cur);
    }
  });
  const alle = [...map.values()];
  const drin = alle.filter((r) => !ausserWertung(season, kind, r.key));
  const raus = alle.filter((r) => ausserWertung(season, kind, r.key)).map((r) => ({ ...r, rank: 0, excluded: true }));
  const ranked = rankAggregates(drin, { points: { win: 1, draw: 0, loss: 0 } } as never) as SeasonRow[];
  return [...ranked, ...raus.sort((a, b) => b.points - a.points)];
}
